import { forwardRef, useCallback } from "react"

// --- Lib ---
import { parseShortcutKeys } from "@/tiptap/lib/tiptap-utils"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

import {
  MENTION_TRIGGER_SHORTCUT_KEY,
  useMentionTrigger,
} from "@/tiptap/ui/mention-trigger-button"

import { Button } from "@/tiptap/ui-primitive/button"
import { Badge } from "@/tiptap/ui-primitive/badge"

export function MentionShortcutBadge({
  shortcutKeys = MENTION_TRIGGER_SHORTCUT_KEY
}) {
  return <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>;
}

/**
 * Button component for inserting mention triggers in a Tiptap editor.
 *
 * For custom button implementations, use the `useMention` hook instead.
 */
export const MentionTriggerButton = forwardRef((
  {
    editor: providedEditor,
    node,
    nodePos,
    text,
    trigger = "@",
    hideWhenUnavailable = false,
    onTriggered,
    showShortcut = false,
    onClick,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const { isVisible, canInsert, handleMention, label, shortcutKeys, Icon } =
    useMentionTrigger({
      editor,
      node,
      nodePos,
      trigger,
      hideWhenUnavailable,
      onTriggered,
    })

  const handleClick = useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    handleMention()
  }, [handleMention, onClick])

  if (!isVisible) {
    return null
  }

  return (
    <Button
      type="button"
      variant="ghost"
      role="button"
      tabIndex={-1}
      disabled={!canInsert}
      data-disabled={!canInsert}
      aria-label={label}
      tooltip={label}
      onClick={handleClick}
      {...buttonProps}
      ref={ref}>
      {children ?? (
        <>
          <Icon className="tiptap-button-icon" />
          {text && <span className="tiptap-button-text">{text}</span>}
          {showShortcut && (
            <MentionShortcutBadge shortcutKeys={shortcutKeys} />
          )}
        </>
      )}
    </Button>
  );
})

MentionTriggerButton.displayName = "MentionTriggerButton"
